import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomTooltip from "../../../../../../../../components/CustomTooltip";
import Flex from "../../../../../../../../components/Flex";
import Text from "../../../../../../../../components/Text";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useClinicalSummaryLink } from "../../../../../../../../queries/encounter/use-clinical-summary-link";
import { usePatientMedicationList } from "../../../../../../../../queries/medication/use-patient-medication-list";
import { ClinicalSummaryLinkRequestClinicalSummaryTypeEnum } from "../../../../../../../../services";
import {
  DEFAULT_KEY_LABEL_COLOR,
  getErrorSnackbar,
  getIdFromObj,
  getSuccessSnackbar,
  getValueFromObj,
  getXtenantId,
} from "../../../../../../../../utils/utils";
import UpsertMedicationsDrawer from "../../medication/UpsertMedicationsDrawer";
import { Medication as MedicationType } from "../../medication/medication-constant";
import ActionableHoverNew from "../ActionableHoverNew";
import { RightAction } from "../PatientRecords";
import { MedicationOptions, paginationForSideBar } from "./SummaryConstant";
import { MedicationTooltip } from "./encounterTooltips/MedicationTooltip";
import { useArchiveState } from "../../../../../../../../hooks/useArchiveState";
import { useArchiveMedicationMutation } from "../../../../../../../../queries/medication/use-patient-medication-archive";
import DeleteModal from "../../../../../../../../components/app/DeleteModal";
import LoadingAndEmptyStateWrapper from "./commonWrappers/LoadingAndEmptyStateWrapper";
import { useRecordsScroll } from "./RecordsScrollContext";
interface props {
  medicationList: MedicationType[];
  loading: boolean;
}

const Medication = ({ medicationList, loading }: props) => {
  const [showPastMedications, setShowPastMedications] = useState(false);
  const medicationDisclosure = useDisclosure();
  const [disabledTooltips, setDisabledTooltips] = useState<{
    [key: number]: boolean;
  }>({});
  const { mutateAsync: linkClinicalDataToAppointment } =
    useClinicalSummaryLink();
  const queryClient = useQueryClient();
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const { mutateAsync: archiveMedication } = useArchiveMedicationMutation();
  const { archiveHandler, deleteDisc } = useArchiveState(archiveMedication);
  const { isScrolling } = useRecordsScroll();

  const clickHandler = (v: string, idx: number) => {
    if (v === "Edit") {
      const row = (medicationList as any)?.data?.content[idx];
      let prescribedBy: { value: string; label: string } | undefined;
      Object.entries(row?.prescribedBy ?? {}).forEach(([value, label]) => {
        prescribedBy = { value, label: label as string };
      });
      medicationDisclosure.open({
        ...row,
        medicalCodes: getIdFromObj(row?.medicalCodes) ?? "",
        prescribedBy,
      });
    }
    if (v === "ExportToNote") {
      linkClinicalDataToAppointment({
        clinicalSummaryLinkRequest: {
          clinicalSummaryType:
            ClinicalSummaryLinkRequestClinicalSummaryTypeEnum.Medication,
          recordId: (medicationList as any)?.data?.content[idx]?.uuid ?? "",
          appointmentId: appointmentid ?? "",
        },
        xTENANTID: getXtenantId(),
      })
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));
          queryClient.invalidateQueries({
            queryKey: ["patient-medication-list"],
          });
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    }
    if (v === "delete") {
      deleteDisc.open((medicationList as any)?.data?.content[idx]?.uuid);
    }
  };

  const handleActionAreaToggle = (idx: number, isHovered: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isHovered }));
  };

  const handleMenuToggle = (idx: number, isOpen: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isOpen }));
  };

  return (
    <>
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId as string}
        onConfirmation={(id) =>
          archiveHandler(true, id).then((res) => {
            queryClient.invalidateQueries({
              queryKey: ["patient-medication-list"],
            });
            return res;
          })
        }
        content={`You want to Delete this Medication`}
      />
      <Flex flexDirection={"column"} width={"100%"} gap={1}>
        <UpsertMedicationsDrawer disclosureHost={medicationDisclosure} />
        <LoadingAndEmptyStateWrapper
          loading={loading}
          isEmpty={(medicationList as any)?.data?.content?.length === 0}
          emptyMessage="No Current Medications found"
        />
        {(medicationList as any)?.data?.content?.map(
          (it: MedicationType, idx: number) => {
            const isTooltipDisabled = disabledTooltips[idx] || false;
            return (
              <Flex key={idx} width={"100%"}>
                <CustomTooltip
                  key={it?.medication?.uuid as string}
                  content={<MedicationTooltip key={idx} data={it as any} />}
                  position="right"
                  disableTooltip={isTooltipDisabled || isScrolling}
                >
                  <ActionableHoverNew
                    renderAction={
                      <div
                        onMouseEnter={() => handleActionAreaToggle(idx, true)}
                        onMouseLeave={() => handleActionAreaToggle(idx, false)}
                      >
                        <RightAction
                          handleClickOption={(v) => clickHandler(v, idx)}
                          menuList={MedicationOptions}
                          onMenuOpen={() => handleMenuToggle(idx, true)}
                          onMenuClose={() => handleMenuToggle(idx, false)}
                        />
                      </div>
                    }
                  >
                    <Flex flexDirection={"column"} width={"100%"} gap={2}>
                      <Text isTabletext color={DEFAULT_KEY_LABEL_COLOR}>
                        {idx + 1}. {getValueFromObj(it?.medicalCodes)} -{" "}
                        {it?.dosage}
                      </Text>
                    </Flex>
                  </ActionableHoverNew>
                </CustomTooltip>
              </Flex>
            );
          }
        )}
        {!loading && (
          <>
            <Button
              onClick={() => setShowPastMedications(!showPastMedications)}
            >
              {showPastMedications
                ? "Hide Past Medications"
                : "Show Past Medications"}
            </Button>
            {showPastMedications && (
              <Flex flexDirection={"column"} width={"100%"} gap={1}>
                <PastMedication />
              </Flex>
            )}
          </>
        )}
      </Flex>
    </>
  );
};

export default Medication;

const PastMedication = () => {
  const { patientId } = useParams() as { patientId: any };
  const { data: pastMedicationsList, isLoading: isPastMedicationsLoading } =
    usePatientMedicationList({
      patientUuid: patientId,
      pagination: paginationForSideBar,
      archive: false,
      active: false,
      enabled: true,
    });
  const medicationDisclosure = useDisclosure();
  const [disabledTooltips, setDisabledTooltips] = useState<{
    [key: number]: boolean;
  }>({});
  const { isScrolling } = useRecordsScroll();

  const clickHandler = (v: string, idx?: number) => {
    if (v === "add") {
      medicationDisclosure.open();
    }
    if (v === "Edit") {
      const row = (pastMedicationsList as any)?.data?.content[idx ?? 0];
      let prescribedBy: { value: string; label: string } | undefined;
      Object.entries(row?.prescribedBy ?? {}).forEach(([value, label]) => {
        prescribedBy = { value, label: label as string };
      });
      medicationDisclosure.open({
        ...row,
        medicalCodes: getIdFromObj(row?.medicalCodes) ?? "",
        prescribedBy,
      });
    }
    // if (v === "exportToNote") {
    // }
    // if (v === "delete") {
    // }
  };

  const handleActionAreaToggle = (idx: number, isHovered: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isHovered }));
  };

  const handleMenuToggle = (idx: number, isOpen: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isOpen }));
  };

  return (
    <Flex flexDirection={"column"} width={"100%"} gap={1}>
      <UpsertMedicationsDrawer disclosureHost={medicationDisclosure} />
      <LoadingAndEmptyStateWrapper
        loading={isPastMedicationsLoading}
        isEmpty={pastMedicationsList?.data?.content?.length === 0}
        emptyMessage="No Past Medications found"
      />
      {(pastMedicationsList as any)?.data?.content?.map(
        (it: MedicationType, idx: number) => {
          const isTooltipDisabled = disabledTooltips[idx] || false;
          return (
            <Flex key={idx} width={"100%"}>
              <CustomTooltip
                key={it?.medication?.uuid as string}
                content={<MedicationTooltip key={idx} data={it as any} />}
                position="right"
                disableTooltip={isTooltipDisabled || isScrolling}
              >
                <ActionableHoverNew
                  renderAction={
                    <div
                      onMouseEnter={() => handleActionAreaToggle(idx, true)}
                      onMouseLeave={() => handleActionAreaToggle(idx, false)}
                    >
                      <RightAction
                        handleClickOption={(v) => clickHandler(v, idx)}
                        menuList={[{ value: "Edit", label: "Edit" }]}
                        onMenuOpen={() => handleMenuToggle(idx, true)}
                        onMenuClose={() => handleMenuToggle(idx, false)}
                      />
                    </div>
                  }
                >
                  <Flex flexDirection={"column"} width={"100%"} gap={2}>
                    <Text color={DEFAULT_KEY_LABEL_COLOR}>
                      {idx + 1}. {getValueFromObj(it?.medicalCodes)} -{" "}
                      {it?.dosage} ({it?.note})
                    </Text>
                  </Flex>
                </ActionableHoverNew>
              </CustomTooltip>
            </Flex>
          );
        }
      )}
    </Flex>
  );
};
